import {Sine} from "gsap";
import {PixiUtils, Random} from "html-living-framework";
import {IGalleryPuppetData} from "../Data/IGalleryPuppetData";
import {array} from "../Utils/array";
import {Container} from "../Utils/Container";
import {Sprite} from "../Utils/Sprite";
import {PuppetItem} from "./PuppetItem";
import {PuppetType} from "./PuppetType";

export class PuppetScreen extends Container {

    public readonly screen = this.addChild(new Sprite());
    public readonly screenLight = this.addChild(new Sprite());
    public readonly itemsContainer = this.addChild(new Container());
    public readonly ropeLeft = this.addChild(new Sprite());
    public readonly ropeRight = this.addChild(new Sprite());
    public readonly items = array((i: number) => {
        const type: PuppetType = i === 3 ? PuppetType.Background :
            i === 4 ? PuppetType.Prop : i;
        const item = new PuppetItem(type);
        this.itemsContainer.addChild(item);
        return item;
    });
    public readonly screenArea = {x: 0, y: 0, width: 0, height: 0};
    private screenLightPosition = {x: 0, y: 0};
    private isFlickering = false;
    private isMovingItems = false;

    constructor() {
        super();
        this.screenLight.visible = false;
    }

    public onLayout() {
        PixiUtils.sendToFront(this.items[PuppetType.Character]);
        this.screenLightPosition.x = this.screenLight.x;
        this.screenLightPosition.y = this.screenLight.y;
    }

    public incrementItem(type: PuppetType) {
        const item = this.items[type];
        const pair = type === PuppetType.Background ? this.items[3] :
            type === PuppetType.Prop ? this.items[4] : undefined;
        if (item) {
            if (pair) {
                if (pair.value >= pair.maxValue) {
                    pair.value = 0;
                    item.value++;
                } else if (item.value >= item.maxValue) {
                    item.value = 0;
                    pair.value++;
                } else if (pair.value > 0) {
                    pair.value++;
                } else {
                    item.value++;
                }
                if (pair.value > 0 && this.isMovingItems) {
                    pair.startMove(this.screenArea.x, this.screenArea.x + this.screenArea.width);
                }
            } else {
                item.value++;
            }
            if (item.value > 0 && this.isMovingItems) {
                item.startMove(this.screenArea.x, this.screenArea.x + this.screenArea.width);
            }
        }
    }

    public hasItems() {
        return this.items.some((it) => it.value > 0);
    }

    public startMove() {
        if (this.isMovingItems) {
            return;
        }
        this.isMovingItems = true;
        this.items.forEach((it) => it.startMove(this.screenArea.x, this.screenArea.x + this.screenArea.width));
    }

    public stopMove() {
        if (!this.isMovingItems) {
            return;
        }
        this.isMovingItems = false;
        this.items.forEach((it) => it.stopMove());
    }

    public flickerLight() {
        if (this.isFlickering) {
            return;
        }
        this.isFlickering = true;
        if (!this.screenLight.visible) {
            this.screenLight.visible = true;
            this.screenLight.alpha = 0;
        }
        this.flickerStep();
    }

    public stopFlickerLight() {
        if (!this.isFlickering) {
            return;
        }
        this.isFlickering = false;
        this.screenLight.killTimelines();
        this.screenLight.newTimeline().to(this.screenLight, 0.15, {alpha: 0});
    }

    public save(): IGalleryPuppetData {
        return {
            items: this.items.map((item) => ({
                value: item.value,
                x: item.x,
                y: item.y,
                flip: item.scale.x < 0,
            })),
        };
    }

    public load(data?: IGalleryPuppetData) {
        if (data && data.items) {
            data.items.forEach((it, i) => {
                const item = this.items[i];
                if (item) {
                    item.x = it.x;
                    item.y = it.y;
                    item.value = it.value;
                    item.scale.x = it.flip ? -1 : 1;
                }
            });
        }
    }

    private flickerStep() {
        this.screenLight.newTimeline()
            .to(this.screenLight, Random.floatBetween(0.1, 0.3), {
                alpha: Random.floatBetween(0.6, 1),
                ease: Sine.easeInOut,
            })
            .to(this.screenLight, Random.floatBetween(0.1, 0.3), {
                alpha: Random.floatBetween(0.3, 0.7),
                ease: Sine.easeInOut,
            })
            .call(() => this.flickerStep());
    }
}
